import { useEffect, useRef, useState } from "react";
import { listPipelines } from "../api";
import { type FelixGraphLite } from "../pipeline";
import { uploadToNexus, runOnNexus, type FelixArtifact, type FelixNodeResult } from "../felix";
import { glyphOf, stepOf, topoNodes } from "../steps";
import { GridLoader, Pill, type Tone } from "./ai/Primitives";
import { IconDownload } from "./icons";

/**
 * PipelineView — docked in the right-column stack. Pick a document/report and
 * generate it on the hosted Nexus runner. The input control adapts to the
 * pipeline:
 *   - declares an `input_schema` → a parameter form (data pipelines, e.g. vigeo)
 *   - has an input node, no schema → file upload (document pipelines)
 *   - neither → just generate.
 */

type RunStatus = Record<string, "running" | "done" | "error">;

function prettyLabel(key: string): string {
  return key.replace(/[_-]+/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

export function PipelineView({ instance }: { instance: string }) {
  const [declared, setDeclared] = useState<FelixGraphLite[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [runStatus, setRunStatus] = useState<RunStatus>({});
  const [artifacts, setArtifacts] = useState<FelixArtifact[]>([]);
  const [runLog, setRunLog] = useState<string[]>([]);
  const [params, setParams] = useState<Record<string, string>>({});
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setLoading(true); setErr(null); setRunStatus({}); setArtifacts([]); setRunLog([]); setParams({});
    listPipelines(instance)
      .then((d) => (d.pipelines as FelixGraphLite[]) || [])
      .catch(() => [] as FelixGraphLite[])
      .then((pls) => {
        setDeclared(pls);
        setSelected((s) => s ?? (pls[0]?.id ?? null));
      })
      .finally(() => setLoading(false));
  }, [instance]);

  const graph: FelixGraphLite | null = declared.find((p) => p.id === selected) ?? declared[0] ?? null;

  // Input mode. A declared input_schema → a parameter form; else an input node
  // means uploaded files; else nothing to provide.
  const schemaProps = graph?.input_schema?.properties;
  const paramKeys = schemaProps ? Object.keys(schemaProps) : [];
  const requiredKeys = graph?.input_schema?.required || [];
  const isParamInput = paramKeys.length > 0;
  const isFileInput = !isParamInput && !!graph?.nodes.some((n) => n.kind === "input");
  const missingRequired = requiredKeys.some((k) => !(params[k] || "").trim());

  const options = declared.map((p) => ({ id: p.id, label: p.name }));

  const resetRun = () => { setErr(null); setRunStatus({}); setArtifacts([]); setRunLog([]); };
  const pickPipeline = (id: string) => { setSelected(id); setParams({}); resetRun(); };

  const stepName = (id: string): string => {
    const n = graph?.nodes.find((x) => x.id === id);
    return n ? stepOf(n.kind, n.label) : "step";
  };

  const buildParams = (): Record<string, unknown> => {
    const out: Record<string, unknown> = {};
    for (const k of paramKeys) {
      const v = (params[k] || "").trim();
      if (!v) continue;
      out[k] = schemaProps?.[k]?.type === "array" ? v.split(",").map((s) => s.trim()).filter(Boolean) : v;
    }
    return out;
  };

  const run = async (opts: { files?: FileList | null; params?: Record<string, unknown> }) => {
    if (!graph) return;
    setBusy("run"); resetRun();
    try {
      const g: FelixGraphLite = JSON.parse(JSON.stringify(graph));
      if (opts.files && opts.files.length) {
        const up = await uploadToNexus(instance, Array.from(opts.files));
        for (const n of g.nodes) if (n.kind === "input") n.config = { ...n.config, files: up.map((f) => f.key) };
        setRunLog((l) => [...l, `uploaded ${up.length} document(s)`]);
      }
      if (opts.params) {
        // Merge the form values into the input node's config — node_input passes
        // non-file config through downstream (felix_run's `inputs` mechanism).
        for (const n of g.nodes) if (n.kind === "input") n.config = { ...n.config, ...opts.params };
      }
      const found: FelixArtifact[] = [];
      // Both the live progress poll and the end-of-run buffered replay report
      // node status, so the log dedupes per node while artifacts (replay-only)
      // and status chips (idempotent sets) apply from either source.
      const logged = new Set<string>();
      await runOnNexus(instance, g, {
        onNodeStart: (id) => { setRunStatus((s) => ({ ...s, [id]: "running" })); },
        onNodeDone: (id, r: FelixNodeResult) => {
          setRunStatus((s) => ({ ...s, [id]: "done" }));
          if (!logged.has(id)) { logged.add(id); setRunLog((l) => [...l, `✓ ${stepName(id)}`]); }
          // The render and output nodes both report the document: keep one per URL.
          for (const a of r.artifacts || []) {
            if (!found.some((f) => (f.url || f.token) === (a.url || a.token))) found.push(a);
          }
          if (r.artifacts?.length) setArtifacts([...found]);
        },
        onNodeError: (id, e) => {
          setRunStatus((s) => ({ ...s, [id]: "error" }));
          // The live poll carries no message (status chip only); the replay does.
          if (e) setRunLog((l) => [...l, `✗ ${stepName(id)}: ${e}`]);
        },
        onRunDone: (status) => { setRunLog((l) => [...l, status === "done" ? "✓ ready" : `finished: ${status}`]); },
      });
    } catch (e) {
      setErr(String((e as Error).message || e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="pipeline-docked">
      <label className="pipeline-pick">
        <span>Report</span>
        <select value={selected ?? ""} disabled={loading || !options.length}
                onChange={(e) => pickPipeline(e.target.value)}>
          {!options.length && <option value="">{loading ? "Loading…" : "Nothing here yet"}</option>}
          {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
        </select>
      </label>

      {graph ? (
        <>
          <PipelineDiagram graph={graph} status={runStatus} />

          {isParamInput && (
            <div className="pipeline-params">
              {paramKeys.map((k) => {
                const spec = schemaProps![k];
                const req = requiredKeys.includes(k);
                const isDate = k === "from" || k === "to" || /\bdate\b/i.test(spec.description || "");
                return (
                  <label key={k} className="pipeline-param">
                    <span>{prettyLabel(k)}{req && <em> *</em>}</span>
                    <input
                      type={isDate ? "date" : "text"}
                      value={params[k] || ""}
                      placeholder={spec.description || ""}
                      onChange={(e) => setParams((p) => ({ ...p, [k]: e.target.value }))}
                    />
                  </label>
                );
              })}
            </div>
          )}

          <div className="pipeline-actions">
            {isFileInput ? (
              <>
                <input ref={fileInput} type="file" multiple style={{ display: "none" }}
                       onChange={(e) => run({ files: e.target.files })} />
                <button className="primary" disabled={busy !== null} onClick={() => fileInput.current?.click()}>
                  {busy === "run" ? "Generating…" : "Upload and generate"}
                </button>
              </>
            ) : (
              <>
                <button className="primary" disabled={busy !== null || (isParamInput && missingRequired)}
                        onClick={() => run({ params: isParamInput ? buildParams() : undefined })}>
                  {busy === "run" ? "Generating…" : "Generate"}
                </button>
                <span className="pipeline-run-hint">
                  {isParamInput ? "Fill in the details — no upload needed." : "No upload needed."}
                </span>
              </>
            )}
          </div>

          {!!artifacts.length && (
            <div className="pipeline-artifacts">
              <div className="grill-spec-list-label">Your documents</div>
              {artifacts.map((a) => (
                <a key={a.url || a.token} href={a.url} download className="pipeline-artifact">
                  <IconDownload size={13} />
                  <span className="pipeline-artifact-name">{a.name}</span>
                  {Number.isFinite(a.bytes) && <span>{Math.round(a.bytes / 1024)} KB</span>}
                </a>
              ))}
            </div>
          )}
          {!!runLog.length && (
            <div className="pipeline-runlog">{runLog.map((l, i) => (
              <div key={i} className={l.startsWith("✗") ? "err" : l.startsWith("✓") ? "ok" : ""}>{l}</div>
            ))}</div>
          )}
          {err && <div className="error-banner">{err}</div>}
        </>
      ) : (
        <div className="pipeline-empty">
          {loading ? <GridLoader label="Loading" /> : "No report selected."}
        </div>
      )}
    </div>
  );
}

/** A node's run state reads by colour before it reads by word. */
const NODE_TONE: Record<string, Tone> = {
  running: "running", done: "done", error: "error",
};
const NODE_LABEL: Record<string, string> = {
  running: "Running", done: "Done", error: "Failed",
};

function PipelineDiagram({ graph, status }: { graph: FelixGraphLite; status: RunStatus }) {
  // The template node feeds the Generate step; for a clean linear view we show
  // only the sequential steps (the chosen document is named in the header),
  // in execution order so the chain lights up left to right as the run proceeds.
  const chain = topoNodes(graph.nodes, graph.edges).filter((n) => n.kind !== "template");
  return (
    <div className="pipeline-diagram">
      <div className="pipeline-chain">
        {chain.map((n, i) => (
          <div key={n.id} className="pipeline-node-wrap">
            <div className={`pipeline-node kind-${n.kind} run-${status[n.id] || "idle"}`}>
              <span className={`pipeline-node-glyph tone-${NODE_TONE[status[n.id]] || "neutral"}`}>
                {glyphOf(n.kind)}
              </span>
              <span className="pipeline-node-label">{stepOf(n.kind, n.label)}</span>
              {status[n.id] && (
                <Pill tone={NODE_TONE[status[n.id]] || "neutral"} dot={status[n.id] === "running"}>
                  {NODE_LABEL[status[n.id]] || status[n.id]}
                </Pill>
              )}
            </div>
            {i < chain.length - 1 && <span className="pipeline-arrow">→</span>}
          </div>
        ))}
      </div>
    </div>
  );
}
